import { Box, ButtonBase, Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colorPalette } from "../../config/colorPalette";
import { ELEVATION_POP } from "../../config/shadows";
import { focusRingSx } from "../_shared/controlStyles";
import Button from "../Button/Button";

const ACTION_DEFAULTS = {
  secondary: { variant: "secondary" },
  primary: { variant: "primary" },
};

const Action = ({ action, kind }) => {
  const { label, ...rest } = action;
  return (
    <Button size="lg" {...ACTION_DEFAULTS[kind]} {...rest}>
      {label}
    </Button>
  );
};

/**
 * Modal
 *
 * A dialog with a title and close icon, a body, and up to two footer buttons.
 * Figma: Brand-SaaS-DS, Modal (13:1142). 420px wide, 20px padding, 16px radius,
 * 16px between the title row, the body and the buttons.
 *
 * Props:
 * - open, onClose
 * - title: header text
 * - children: the body (grey 14px text by default; scrolls past maxHeight)
 * - secondaryAction / primaryAction: { label, onClick, disabled?, variant?, sx? }. The
 *   buttons share the footer width equally, 12px apart; variant is any Button variant
 *   (secondary and primary by default).
 * - width: dialog width in px (default 420)
 * - bodyRef: ref for the scrolling body (e.g. for infinite scroll)
 * - bodyProps: extra props for the body element (role, aria-label, ...)
 * - bodySx: extra styles for the body, merged last
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  secondaryAction,
  primaryAction,
  width = 420,
  bodyRef,
  bodyProps = {},
  bodySx = {},
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    PaperProps={{
      sx: {
        width,
        maxWidth: "calc(100% - 32px)",
        margin: "16px",
        padding: "20px",
        borderRadius: "16px",
        boxSizing: "border-box",
        boxShadow: ELEVATION_POP,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        overflow: "hidden",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: '"Lexend", sans-serif',
          fontSize: 18,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: "-0.1px",
          color: colorPalette.mercury[950],
        }}
      >
        {title}
      </Box>
      <ButtonBase
        aria-label="Close"
        onClick={onClose}
        sx={{
          flexShrink: 0,
          width: 20,
          height: 20,
          color: colorPalette.mercuryAlpha[65],
          "&.Mui-focusVisible": focusRingSx,
        }}
      >
        <CloseIcon aria-hidden="true" sx={{ fontSize: 20 }} />
      </ButtonBase>
    </Box>

    <Box
      ref={bodyRef}
      {...bodyProps}
      sx={{
        maxHeight: 400,
        overflowY: "auto",
        fontFamily: '"Lexend", sans-serif',
        fontSize: 14,
        lineHeight: 1.45,
        letterSpacing: "0.1px",
        color: colorPalette.mercuryAlpha[65],
        ...bodySx,
      }}
    >
      {children}
    </Box>

    {(secondaryAction || primaryAction) && (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            secondaryAction && primaryAction ? "1fr 1fr" : "1fr",
          gap: "12px",
        }}
      >
        {secondaryAction && (
          <Action action={secondaryAction} kind="secondary" />
        )}
        {primaryAction && <Action action={primaryAction} kind="primary" />}
      </Box>
    )}
  </Dialog>
);

export default Modal;
